import { ClientConfig } from "@/models/clientConfig";
import { defineStore } from "pinia";
import { ref } from "vue";
import { mainServices } from "@/services/mainServices";
import { useAuthStore } from "@/stores/authStore";

export const useRequirementsStore = defineStore("requirements", () => {
  const requirementsData = ref({
    medicationRequirements: null,
    diagnosisRequirements: null,
    payerQuestions: null,
    stepTherapyRequirements: null,
    insurancePlanTypes: null,
  });
  const clientConfig = ref(null);
  // True when clientConfig holds the config of the user's ACTIVE (acting) company; false when an
  // intake review page loaded the viewed order's company into the shared slot. Own-navigation
  // gating (sidebar Intake, router guard) must not trust a viewed-company config.
  const clientConfigIsActiveCompany = ref(false);
  // Guards against out-of-order responses: only the latest refresh commits clientConfig and its
  // provenance flag, so a slow viewed-company response can't land after a newer current-user one.
  let configRequestSeq = 0;
  // Kept out of the shared clientConfig slot so a company-scoped refresh can't wipe them. null = not loaded.
  const supportTicketDashboardEnabled = ref(null);
  const billingEnabled = ref(null);
  const reportsDashboardEnabled = ref(null);
  const visibleModules = ref(null);
  const worklistEnabled = ref(null);
  const escalationsEnabled = ref(null);
  const missionControlEnabled = ref(null);
  const standaloneStarts = ref([]);

  async function refreshClientConfig(clientCompanyId) {
    const seq = ++configRequestSeq;
    clientConfig.value = null;
    clientConfigIsActiveCompany.value = false;
    const response = await mainServices.getClientConfig(clientCompanyId);
    if (seq !== configRequestSeq) return;
    clientConfig.value = new ClientConfig(response, clientCompanyId);
  }

  async function refreshCurrentUserClientConfig() {
    const seq = ++configRequestSeq;
    // A company-less provider login (LAM-5263) has no client config; the endpoint 400s. Skip the
    // fetch centrally so no panel-body form-preview throws an uncaught rejection on mount — but
    // still drop any config left behind by a previous tenant/admin session, or consumers keep
    // reading its flags (e.g. isAdminCompany would leave the video library's edit controls up).
    if (useAuthStore().userInfo?.isProvider) {
      clientConfig.value = null;
      clientConfigIsActiveCompany.value = false;
      supportTicketDashboardEnabled.value = false;
      billingEnabled.value = false;
      reportsDashboardEnabled.value = false;
      visibleModules.value = null;
      worklistEnabled.value = false;
      escalationsEnabled.value = false;
      missionControlEnabled.value = false;
      standaloneStarts.value = [];
      return;
    }
    clientConfig.value = null;
    clientConfigIsActiveCompany.value = false;
    const response = await mainServices.getCurrentUserClientConfig();
    supportTicketDashboardEnabled.value = response.supportTicketDashboardEnabled ?? false;
    billingEnabled.value = response.billingEnabled ?? false;
    reportsDashboardEnabled.value = response.reportsDashboardEnabled ?? false;
    if (seq !== configRequestSeq) return;
    visibleModules.value = Array.isArray(response.visibleModules) ? response.visibleModules : null;
    worklistEnabled.value = response.uiConfig?.worklistEnabled ?? false;
    escalationsEnabled.value = response.uiConfig?.escalationsEnabled ?? false;
    missionControlEnabled.value = response.uiConfig?.missionControlEnabled ?? false;
    standaloneStarts.value = Array.isArray(response.standaloneStarts) ? response.standaloneStarts : [];
    clientConfig.value = new ClientConfig(response, response.clientCompanyId);
    clientConfigIsActiveCompany.value = true;
  }

  async function ensureSupportTicketDashboardFlag() {
    if (useAuthStore().userInfo?.isProvider) return false;
    if (typeof supportTicketDashboardEnabled.value === "boolean") {
      return supportTicketDashboardEnabled.value;
    }
    const response = await mainServices.getCurrentUserClientConfig();
    supportTicketDashboardEnabled.value = response.supportTicketDashboardEnabled ?? false;
    return supportTicketDashboardEnabled.value;
  }

  return {
    requirementsData,
    clientConfig,
    clientConfigIsActiveCompany,
    supportTicketDashboardEnabled,
    billingEnabled,
    reportsDashboardEnabled,
    visibleModules,
    worklistEnabled,
    escalationsEnabled,
    missionControlEnabled,
    standaloneStarts,
    refreshClientConfig,
    refreshCurrentUserClientConfig,
    ensureSupportTicketDashboardFlag,
  };
});
